import { Collection } from "@tblabs/truffle";
import { PaymentPresets } from "../../Models/OrderTicket/PaymentForm";
import { Ticket } from "../../Models/OrderTicket/Ticket";
import { RawRentTicketBasketItem, RawSaleTicketBasketItem, RawTicketBasketItem } from "../../Models/OrderTicket/RawTicketBasketItem";
import { BasketItem } from "../../Services/Basket/BasketItem";
import { BasketItemType } from "../../Models/Basket/BasketItemType";
import { v4 as uuidv4 } from 'uuid';
import { PaymentForm } from "./PaymentForm";
import { BasketFormLogic } from "./BasketFormLogic";
import { SaleBasketItem } from "../../Services/Basket/SaleBasketItem";
import { RentBasketItem } from "../../Services/Basket/RentBasketItem";
import { RawProductInfo } from "../../Models/OrderTicket/RawProductInfo";


export class BasketToTicketConverter
{
    public Convert(info: BasketFormLogic): Ticket
    {
        const ticket = new Ticket();

        ticket.Id = uuidv4().toUpperCase();

        ticket.Meta.CreatedAt = new Date();
        ticket.Meta.CreatedBy = window.location.hostname;

        ticket.BasketList = this.MakeBasket(info.basket.Items);

        ticket.BasketInfo.PriceBeforeDiscount = info.basket.TotalPriceBeforeDiscounts.value;
        ticket.BasketInfo.PriceAfterDiscount = info.basket.TotalPriceAfterDiscounts.value;
        ticket.BasketInfo.Discounts = info.basket.Discounter.GetActiveCosts().map(x => x.AsRaw);

        ticket.Customer.Name = info.Customer.Name.value;
        ticket.Customer.Phone = info.Customer.Phone.value;
        ticket.Customer.Email = info.Customer.Email.value;
        ticket.Customer.Address = info.Customer.Address.AsString;
        ticket.Customer.Experience = info.Customer.Experience.value;
        ticket.Customer.BankAccountNumber = info.Customer.DepositAccount.value;
        ticket.Customer.Origin = info.Customer.Origin.value;

        ticket.Delivery = info.DeliveryForm.value;
        ticket.Return = info.ReturnForm.value;
        ticket.WithTraining = info.WithTraining.value;
        ticket.PickupPlace = info.PickupSpot.value;
        ticket.Notes = info.Notes.value;

        switch (info.PaymentForm.value)
        {
            case PaymentPresets.AllWithCash:
                ticket.DepositPaymentForm = PaymentForm.Cash;
                ticket.BasketPaymentForm = PaymentForm.Cash;
                break;
            case PaymentPresets.DepositWithBankTransfer_ServiceWithCash:
                ticket.DepositPaymentForm = PaymentForm.BankTransfer;
                ticket.BasketPaymentForm = PaymentForm.Cash;
                break;
            case PaymentPresets.DepositWithBlik_ServiceWithCash:
                ticket.DepositPaymentForm = PaymentForm.Blik;
                ticket.BasketPaymentForm = PaymentForm.Cash;
                break;
            case PaymentPresets.AllWithBankTransfer:
                ticket.DepositPaymentForm = PaymentForm.BankTransfer;
                ticket.BasketPaymentForm = PaymentForm.BankTransfer;
                break;
            case PaymentPresets.AllWithCrypto:
                ticket.DepositPaymentForm = PaymentForm.Crypto;
                ticket.BasketPaymentForm = PaymentForm.Crypto;
                break;
            case PaymentPresets.CashAtDelivery:
                ticket.DepositPaymentForm = PaymentForm.CashAtDelivery; // NOT USED ANYWAY
                ticket.BasketPaymentForm = PaymentForm.CashAtDelivery;
                break;
        }

        return ticket;
    }

    private MakeBasket(basketItems: Collection<BasketItem>): RawTicketBasketItem[]
    {
        return basketItems.Items.map(item =>
        {
            switch (item.Type)
            {
                case BasketItemType.Sale:
                    const saleItem = item as SaleBasketItem;
                    return {
                        Type: saleItem.Type,
                        ProductInfo: this.MakeProductInfo(saleItem),
                        TotalPrice: saleItem.FinalPriceAfterDiscount.value,
                        Quantity: saleItem.Quantity.value,
                    } as RawSaleTicketBasketItem;

                case BasketItemType.Rent:
                    const rentItem = item as RentBasketItem;
                    return {
                        Type: rentItem.Type,
                        Quantity: rentItem.Quantity.value,
                        TotalPrice: rentItem.FinalPriceAfterDiscount.value,
                        ProductInfo: this.MakeProductInfo(rentItem),
                        Option: { Id: rentItem.ProductOptionId.value, Name: rentItem.Product?.Options.GetById(rentItem.ProductOptionId.value).Label.value ?? "" },
                        From: rentItem.Dates.Start.value,
                        To: rentItem.Dates.End.value,
                        Deposit: rentItem.FinalDeposit.value,
                    } as RawRentTicketBasketItem;

                default:
                    throw new Error(`Unhandled basket item type: ${item.Type}`);
            }
        });
    }

    private MakeProductInfo(x: BasketItem): RawProductInfo
    {
        return {
            Origin: window.location.origin,
            Id: x.Product?.Id ?? "",
            Name: x.Product?.BasketName.value ?? "",
            Link: `${window.location.origin}#produkt/${x.Product?.Url.value}/${x.ProductOptionId.value}`,
            Category: x.Product?.Category.Id.value ?? "",
            Picture: x.Product?.Images?.Items[0]?.ThumbnailUrl?.value ?? "",
            Tutorial: x.Product?.TutorialLink.value ?? "",
            Prices: x.Product?.RentPrices.Items.map(x => `${x.Value.value}zł/${x.Days.value}dni`).join(" ; ") + " ; " + x.Product?.SalePrice.value + "zł/szt",
        }
    }
}
